import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../utils/translationDictionaries';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslations(language);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setAboutDropdownOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    setAboutDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setAboutDropdownOpen(false);
  };

  const toggleAboutDropdown = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setAboutDropdownOpen((prev) => !prev);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/realty-website-764ee.firebasestorage.app/o/web-media%2Farg_logo_nobg.png?alt=media&token=53b347a3-8d9e-4e5a-8a1f-64b7cbc32e69" 
            alt="Alicea Realty Group LLC Logo" 
            className="navbar-logo-img"
          />
        </Link>
        <button 
          type="button"
          className="navbar-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-nav-drawer"
        >
          <span className="navbar-toggle-icon"></span>
          <span className="navbar-toggle-icon"></span>
          <span className="navbar-toggle-icon"></span>
        </button>
        {isMobileMenuOpen && (
          <div className="mobile-nav-overlay" onClick={closeMobileMenu}>
            <div
              className="mobile-nav-drawer"
              id="mobile-nav-drawer"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mobile-nav-header">
                <div className="mobile-nav-title">{t(language === 'es' ? 'Páginas' : 'Pages')}</div>
                <button
                  type="button"
                  className="mobile-nav-close"
                  onClick={closeMobileMenu}
                  aria-label="Close navigation menu"
                >
                  ×
                </button>
              </div>

              <div className="mobile-nav-links">
                <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.home')}</Link>
                <Link to="/properties/for-sale" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.forSale')}</Link>
                <Link to="/properties/for-rent" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.forRent')}</Link>
                <Link to="/properties/sold" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.sold')}</Link>
                <Link to="/reposeidas" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.reposeidas')}</Link>
                <Link to="/resources" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.resources')}</Link>
                <Link to="/mls" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.mls')}</Link>

                <details className="mobile-nav-details">
                  <summary className="mobile-nav-summary">{t('navbar.aboutUs')}</summary>
                  <div className="mobile-nav-submenu">
                    <Link to="/about" className="mobile-nav-sublink" onClick={closeMobileMenu}>{t('navbar.aboutUs')}</Link>
                    <Link to="/our-team" className="mobile-nav-sublink" onClick={closeMobileMenu}>{t('navbar.ourTeam')}</Link>
                  </div>
                </details>

                <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.contact')}</Link>

                {user && (
                  <>
                    <Link to="/admin" className="mobile-nav-link" onClick={closeMobileMenu}>{t('navbar.dashboard')}</Link>
                    <button onClick={() => { logout(); closeMobileMenu(); }} className="mobile-nav-action">{t('navbar.logout')}</button>
                  </>
                )}

                <button 
                  onClick={toggleLanguage} 
                  className="mobile-nav-action"
                  aria-label={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                  title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                >
                  {language === 'es' ? 'Translate to English' : 'Traducir al Español'}
                </button>

                <button 
                  onClick={() => window.location.href = "tel:+17873421648"}
                  className="mobile-nav-action"
                  aria-label={'Llamar al 787-342-1648'}
                  title={'787-342-1648'}
                >
                  787-342-1648
                </button>
              </div>
            </div>
          </div>
        )}
        <ul className={`navbar-menu ${isMobileMenuOpen ? 'navbar-menu-open' : ''}`}>
          <li className="navbar-item">
            <Link to="/" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.home')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/properties/for-sale" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.forSale')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/properties/for-rent" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.forRent')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/properties/sold" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.sold')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/reposeidas" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.reposeidas')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/resources" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.resources')}</Link>
          </li>
          <li className="navbar-item">
            <Link to="/mls" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.mls')}</Link>
          </li>
          <li
            className="navbar-item navbar-dropdown"
            onMouseEnter={() => setAboutDropdownOpen(true)}
            onMouseLeave={() => setAboutDropdownOpen(false)}
          >
            <button
              type="button"
              className="navbar-link navbar-dropdown-toggle"
              onClick={toggleAboutDropdown}
              aria-expanded={aboutDropdownOpen}
              aria-haspopup="true"
            >
              {t('navbar.aboutUs')}
              <span className="dropdown-arrow">▼</span>
            </button>
            <ul 
              className={`navbar-dropdown-menu ${aboutDropdownOpen ? 'navbar-dropdown-open' : ''}`}
            >
              <li className="navbar-dropdown-item">
                <Link to="/about" className="navbar-dropdown-link" onClick={closeMobileMenu}>{t('navbar.aboutUs')}</Link>
              </li>
              <li className="navbar-dropdown-item">
                <Link to="/our-team" className="navbar-dropdown-link" onClick={closeMobileMenu}>{t('navbar.ourTeam')}</Link>
              </li>
            </ul>
          </li>
          {/* <li className="navbar-item navbar-dropdown">
  <button
    type="button"
    className="navbar-link navbar-dropdown-toggle"
    onClick={() => setAboutDropdownOpen(prev => !prev)}
  >
    {t('navbar.aboutUs')}
    <span className="dropdown-arrow">▼</span>
  </button>

  <ul 
    className={`navbar-dropdown-menu ${aboutDropdownOpen ? 'navbar-dropdown-open' : ''}`}
  >
    <li className="navbar-dropdown-item">
      <Link to="/about" className="navbar-dropdown-link" onClick={closeMobileMenu}>
        {t('navbar.aboutUs')}
      </Link>
    </li>
    <li className="navbar-dropdown-item">
      <Link to="/our-team" className="navbar-dropdown-link" onClick={closeMobileMenu}>
        {t('navbar.ourTeam')}
      </Link>
    </li>
  </ul>
          </li> */}
          <li className="navbar-item">
            <Link to="/contact" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.contact')}</Link>
          </li>
          {user && (
            <>
              <li className="navbar-item">
                <Link to="/admin" className="navbar-link" onClick={closeMobileMenu}>{t('navbar.dashboard')}</Link>
              </li>
              <li className="navbar-item">
                <button onClick={() => { logout(); closeMobileMenu(); }} className="navbar-button">{t('navbar.logout')}</button>
              </li>
            </>
          )}
          <li className="navbar-item">
            <button 
              onClick={toggleLanguage} 
              className="language-toggle-button"
              aria-label={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
              title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
            >
              {language === 'es' ? 'EN' : 'ES'}
            </button>
          </li>
          <li className="navbar-item">
            <button 
              onClick={() => window.location.href = "tel:+17873421648"}
              className="call-button"
              aria-label={'Llamar al 787-342-1648'}
              title={'787-342-1648'}
            >
              787-342-1648
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
